import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertTriangle, ArrowRight, FileText, Scale, ShieldCheck } from "lucide-react";

import { PageHeader } from "@/components/page-header";
import { PageLayout } from "@/components/page-layout";
import { Button } from "@super-resume-new/ui/components/button";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/terms")({
  head: () =>
    seo({
      title: "Terms and Conditions | JobCues",
      description:
        "Terms of Service and Conditions for JobCues. Review account guidelines, fair usage policies, and credit abuse restrictions.",
      path: "/terms",
    }),
  component: TermsPageComponent,
});

const SECTIONS = [
  { id: "section-1", num: "01", title: "Acceptance of terms" },
  { id: "section-2", num: "02", title: "Description of service" },
  { id: "section-3", num: "03", title: "User accounts and fair use" },
  { id: "section-4", num: "04", title: "Credits, pricing, and payments" },
  { id: "section-5", num: "05", title: "User content and career honesty" },
  { id: "section-6", num: "06", title: "Prohibited conduct" },
  { id: "section-7", num: "07", title: "Disclaimer and liability" },
  { id: "section-8", num: "08", title: "Modifications and contact" },
];

function TermsPageComponent() {
  const lastUpdated = "August 24, 2026";

  return (
    <PageLayout id="terms-page-content" className="max-w-7xl">
      <PageHeader
        title="Terms and conditions"
        description={<>Last updated {lastUpdated}</>}
        icon={Scale}
      />

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
        {/* Navigation Sidebar */}
        <aside className="lg:col-span-4 xl:col-span-3">
          <div className="sticky top-20 flex flex-col gap-5">
            <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
              <h2 className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <FileText className="size-3.5 text-muted-foreground" aria-hidden="true" />
                Table of contents
              </h2>
              <nav className="flex flex-col space-y-1">
                {SECTIONS.map((sec) => (
                  <a
                    key={sec.id}
                    href={`#${sec.id}`}
                    className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-[13px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    <span className="font-mono text-[11px] text-muted-foreground">{sec.num}</span>
                    <span className="truncate">{sec.title}</span>
                  </a>
                ))}
              </nav>
            </div>

            <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-5 shadow-sm">
              <div className="flex items-center gap-2">
                <ShieldCheck className="size-4 text-violet-500" aria-hidden="true" />
                <h3 className="text-xs font-semibold text-foreground">Privacy policy</h3>
              </div>
              <p className="text-[13px] leading-relaxed text-muted-foreground">
                Learn how your personal details, career data, and resumes are protected.
              </p>
              <Link to="/privacy" className="mt-1">
                <Button variant="outline" size="sm" className="w-full justify-between text-xs">
                  <span>View privacy policy</span>
                  <ArrowRight className="size-3 text-muted-foreground" aria-hidden="true" />
                </Button>
              </Link>
            </div>
          </div>
        </aside>

        {/* Terms Main Content */}
        <div className="flex flex-col gap-6 lg:col-span-8 xl:col-span-9">
          {/* Section 1 */}
          <section
            id="section-1"
            className="scroll-mt-20 rounded-xl border border-border bg-card p-6 shadow-sm sm:p-8"
          >
            <div className="mb-3 flex items-center gap-2.5">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border bg-muted font-mono text-[11px] font-semibold text-foreground">
                1
              </span>
              <h2 className="text-sm font-semibold text-foreground">Acceptance of terms</h2>
            </div>
            <p className="text-[13px] leading-relaxed text-muted-foreground">
              Using JobCues (the &quot;Service&quot;) means you agree to these Terms. This covers
              the website, resume tailoring, ATS tools, the jobs directory, and the API and MCP
              endpoints. If anything here doesn&apos;t work for you, we&apos;d rather you not use
              the service than use it under terms you disagree with.
            </p>
          </section>

          {/* Section 2 */}
          <section
            id="section-2"
            className="scroll-mt-20 rounded-xl border border-border bg-card p-6 shadow-sm sm:p-8"
          >
            <div className="mb-3 flex items-center gap-2.5">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border bg-muted font-mono text-[11px] font-semibold text-foreground">
                2
              </span>
              <h2 className="text-sm font-semibold text-foreground">Description of service</h2>
            </div>
            <p className="mb-3 text-[13px] leading-relaxed text-muted-foreground">
              JobCues provides an AI-powered suite of career optimization tools, including:
            </p>
            <ul className="list-disc space-y-1.5 pl-5 text-[13px] leading-relaxed text-muted-foreground">
              <li>
                <strong className="font-semibold text-foreground">Resume tailor:</strong> Keyword
                alignment, bullet point restructuring, and evidence verification based on target job
                descriptions.
              </li>
              <li>
                <strong className="font-semibold text-foreground">ATS booster:</strong> PDF keyword
                injection and metadata enhancement for Applicant Tracking System scanners.
              </li>
              <li>
                <strong className="font-semibold text-foreground">
                  Jobs directory &amp; tracker:
                </strong>{" "}
                Curated job postings, match scoring, and stage-by-stage application management.
              </li>
              <li>
                <strong className="font-semibold text-foreground">Developer API &amp; MCP:</strong>{" "}
                Programmatic access and assistant integrations via Model Context Protocol.
              </li>
            </ul>
          </section>

          {/* Section 3 - Multi Account Abuse Highlight */}
          <section
            id="section-3"
            className="scroll-mt-20 rounded-xl border border-border bg-card p-6 shadow-sm sm:p-8"
          >
            <div className="mb-3 flex items-center gap-2.5">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border bg-muted font-mono text-[11px] font-semibold text-foreground">
                3
              </span>
              <h2 className="text-sm font-semibold text-foreground">
                User accounts, multi-account policy, and abuse prevention
              </h2>
            </div>
            <div className="space-y-3.5 text-[13px] leading-relaxed text-muted-foreground">
              <p>
                To access core features of the Service, you must create an account via our supported
                OAuth identity providers (such as Google, GitHub, X, or LinkedIn). You are
                responsible for maintaining the confidentiality and security of your account
                authentication tokens.
              </p>

              <div className="rounded-xl border border-border bg-muted/40 p-4">
                <h3 className="mb-1.5 flex items-center gap-2 text-[13px] font-semibold text-foreground">
                  <AlertTriangle className="size-3.5 shrink-0 text-amber-500" aria-hidden="true" />
                  Please use one account
                </h3>
                <p className="text-[13px] leading-relaxed text-muted-foreground">
                  Free credits exist to let you try the service before paying. Creating extra
                  accounts to collect them again isn&apos;t fair to other users, so we ask that you
                  keep to one account per person. We do track patterns that suggest this is
                  happening, and accounts involved may be suspended.
                </p>
              </div>

              <p>
                You agree to notify us immediately if you suspect unauthorized access to your
                account. JobCues is not liable for losses caused by unauthorized use of your
                credentials.
              </p>
            </div>
          </section>

          {/* Section 4 */}
          <section
            id="section-4"
            className="scroll-mt-20 rounded-xl border border-border bg-card p-6 shadow-sm sm:p-8"
          >
            <div className="mb-3 flex items-center gap-2.5">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border bg-muted font-mono text-[11px] font-semibold text-foreground">
                4
              </span>
              <h2 className="text-sm font-semibold text-foreground">
                Credits, pricing, and payment terms
              </h2>
            </div>
            <div className="space-y-3 text-[13px] leading-relaxed text-muted-foreground">
              <p>
                Certain actions on JobCues (such as AI resume rewrites, ATS score analysis, and PDF
                processing) consume credits. Promotional or free signup credits are granted at our
                sole discretion and may expire or be modified at any time.
              </p>
              <p>
                Credit pack purchases and subscriptions are processed securely through our
                authorized payment partner (Dodo Payments). All purchases are final and
                non-refundable once credits have been consumed or partially utilized, except where
                mandated by applicable consumer protection laws.
              </p>
              <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4 dark:border-amber-900/40 dark:bg-amber-950/20">
                <p className="font-semibold text-foreground">Personal use only</p>
                <p className="mt-1.5">
                  All plans are for your own job search. That means you, applying to jobs for
                  yourself. Using JobCues to power a commercial product, offer resume services to
                  others, resell generated content, or run bulk automation on behalf of other
                  people is outside what these plans cover. If that&apos;s what you need, get in
                  touch and we can talk about it. Otherwise, accounts used that way may be closed.
                </p>
              </div>
            </div>
          </section>

          {/* Section 5 */}
          <section
            id="section-5"
            className="scroll-mt-20 rounded-xl border border-border bg-card p-6 shadow-sm sm:p-8"
          >
            <div className="mb-3 flex items-center gap-2.5">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border bg-muted font-mono text-[11px] font-semibold text-foreground">
                5
              </span>
              <h2 className="text-sm font-semibold text-foreground">
                User content, ownership, and career honesty
              </h2>
            </div>
            <div className="space-y-3 text-[13px] leading-relaxed text-muted-foreground">
              <p>
                You retain all ownership rights and intellectual property in your original career
                data, resumes, cover letters, and professional histories uploaded to the Service.
              </p>
              <p>
                By uploading content, you grant JobCues a non-exclusive, worldwide, royalty-free
                license solely to process, parse, restructure, and render your documents to deliver
                the requested optimization services.
              </p>
              <p>
                JobCues restructures your bullets and suggests keyword alignment, but it works with
                what you give it. Before you send a resume anywhere, read it over and make sure
                every claim is accurate. We&apos;re helping you present real experience more
                clearly, not invent experience that isn&apos;t there.
              </p>
            </div>
          </section>

          {/* Section 6 */}
          <section
            id="section-6"
            className="scroll-mt-20 rounded-xl border border-border bg-card p-6 shadow-sm sm:p-8"
          >
            <div className="mb-3 flex items-center gap-2.5">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border bg-muted font-mono text-[11px] font-semibold text-foreground">
                6
              </span>
              <h2 className="text-sm font-semibold text-foreground">Prohibited conduct</h2>
            </div>
            <p className="mb-2 text-[13px] leading-relaxed text-muted-foreground">
              A few things we ask you not to do:
            </p>
            <ul className="list-disc space-y-1.5 pl-5 text-[13px] leading-relaxed text-muted-foreground">
              <li>
                Reverse engineer or attempt to extract source code from our AI models or services.
              </li>
              <li>
                Disrupt or interfere with the service or its server infrastructure.
              </li>
              <li>
                Run scrapers, bots, or automated crawlers against the job directory or APIs without
                an API key.
              </li>
              <li>Send malicious code or disruptive payloads of any kind.</li>
              <li>
                Use the service for anything unlawful, misleading, or intended to harm others.
              </li>
              <li>
                Use the API, MCP server, or any programmatic access to build commercial products,
                offer resume services to others, or resell outputs. This falls outside what the
                plans are designed for, and accounts used this way may be closed.
              </li>
            </ul>
          </section>

          {/* Section 7 */}
          <section
            id="section-7"
            className="scroll-mt-20 rounded-xl border border-border bg-card p-6 shadow-sm sm:p-8"
          >
            <div className="mb-3 flex items-center gap-2.5">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border bg-muted font-mono text-[11px] font-semibold text-foreground">
                7
              </span>
              <h2 className="text-sm font-semibold text-foreground">
                Disclaimer of warranties and limitation of liability
              </h2>
            </div>
            <div className="space-y-3 text-[13px] leading-relaxed text-muted-foreground">
              <p>
                JobCues is a tool to help you put your best case forward. It is not a guarantee of
                interviews or offers. Hiring decisions are made by employers, and we have no
                influence over them.
              </p>
              <p>
                We do our best to keep the service running well, but we can&apos;t promise
                uninterrupted availability. To the extent allowed by law, JobCues is not liable for
                losses or damages arising from your use of the service.
              </p>
            </div>
          </section>

          {/* Section 8 */}
          <section
            id="section-8"
            className="scroll-mt-20 rounded-xl border border-border bg-card p-6 shadow-sm sm:p-8"
          >
            <div className="mb-3 flex items-center gap-2.5">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border bg-muted font-mono text-[11px] font-semibold text-foreground">
                8
              </span>
              <h2 className="text-sm font-semibold text-foreground">Modifications and contact</h2>
            </div>
            <div className="space-y-3 text-[13px] leading-relaxed text-muted-foreground">
              <p>
                We may update these terms from time to time. When we do, the date at the top of
                this page will change. Continuing to use JobCues after an update means you&apos;re
                okay with the revised terms.
              </p>
              <p>
                If you have questions regarding these Terms, please reach out through our official
                support channels or view our{" "}
                <Link
                  to="/privacy"
                  className="font-medium text-foreground underline underline-offset-4 hover:text-violet-500"
                >
                  Privacy policy
                </Link>
                .
              </p>
            </div>
          </section>
        </div>
      </div>
    </PageLayout>
  );
}
